import React from 'react'
import { alpha, Box, ButtonBase, Typography } from '@mui/material'

export interface ThemeDistributionItem {
  code: string
  name: string
  count: number
  pct: number
}

interface ThemeDistributionProps {
  items: ThemeDistributionItem[]
  totalResponses: number
  selectedCode?: string
  /** Omit for a read-only breakdown (sentiment, emotion). */
  onSelectTheme?: (code: string | undefined) => void
  title?: string
  /** Drops the title and caption, where tabs or an info box already say them. */
  bare?: boolean
  /** A colour per row (a theme, a sentiment, an emotion); rows without one use the info colour. */
  colorFor?: (item: ThemeDistributionItem) => string | undefined
}

const formatPct = (pct: number) => (pct >= 10 || Number.isInteger(pct) ? `${Math.round(pct)}%` : `${pct.toFixed(1)}%`)

// One row per theme: name, bar, share of responses and count. Each row is a
// button — choosing one filters the response list beneath it to the
// responses coded with that theme (the drill-through). Themes are
// multi-response, so the bars are scaled to the largest share rather than to
// 100%, and the shares can add up to more than 100%.
const ThemeDistribution: React.FC<ThemeDistributionProps> = ({ items, totalResponses, selectedCode, onSelectTheme, title = 'Themes', bare = false, colorFor }) => {
  const maxPct = Math.max(...items.map((item) => item.pct), 1)

  return (
    <Box>
      {!bare && (
        <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: 1.5, gap: 2 }}>
          <Typography variant="subtitle2">{title}</Typography>
          <Typography variant="caption" color="text.secondary">
            % of {totalResponses.toLocaleString()} responses{onSelectTheme ? ' · a response can carry several themes' : ''}
          </Typography>
        </Box>
      )}
      <Box role="list" sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
        {items.map((item) => {
          const colour = colorFor?.(item)
          const selected = item.code === selectedCode
          const dimmed = selectedCode !== undefined && !selected
          const empty = item.count === 0
          return (
            <ButtonBase
              key={item.code}
              role="listitem"
              aria-pressed={selected}
              aria-label={`${item.name}: ${formatPct(item.pct)}, ${item.count} responses.${onSelectTheme ? (selected ? ' Showing these responses.' : ' Show these responses.') : ''}`}
              disabled={empty || !onSelectTheme}
              onClick={() => onSelectTheme?.(selected ? undefined : item.code)}
              sx={(theme) => ({
                display: 'grid',
                gridTemplateColumns: '1fr auto 44px',
                gridTemplateAreas: '"name pct count" "bar bar bar"',
                alignItems: 'baseline',
                columnGap: 1.5,
                rowGap: 0.75,
                px: 1,
                py: 1,
                borderRadius: 1,
                textAlign: 'left',
                opacity: dimmed ? 0.55 : 1,
                backgroundColor: selected ? alpha(colour ?? theme.palette.info.main, 0.12) : 'transparent',
                transition: 'background-color 150ms ease-out, opacity 150ms ease-out',
                '&.Mui-disabled': { opacity: dimmed ? 0.55 : 1 },
                '&:hover': { backgroundColor: alpha(theme.palette.info.main, selected ? 0.14 : 0.06) },
                '&.Mui-focusVisible': { outline: `2px solid ${theme.palette.info.main}`, outlineOffset: 1 },
              })}
            >
              <Typography variant="body2" sx={{ gridArea: 'name', fontWeight: selected ? 600 : 400, minWidth: 0 }}>
                {item.name}
              </Typography>
              <Typography variant="body2" sx={{ gridArea: 'pct', textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>
                {formatPct(item.pct)}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ gridArea: 'count', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                {empty ? 'none' : item.count.toLocaleString()}
              </Typography>
              <Box sx={(theme) => ({ gridArea: 'bar', height: 6, borderRadius: 3, backgroundColor: alpha(theme.palette.text.primary, 0.06), overflow: 'hidden' })}>
                <Box
                  sx={{
                    height: '100%',
                    width: `${(item.pct / maxPct) * 100}%`,
                    borderRadius: 3,
                    // No grow-in animation: each bar carried its own keyframes
                    // and replayed them on every tab switch, which read as slow
                    // (Adam, 24 Sep). The bars draw at their size straight away.
                    backgroundColor: colour ?? 'info.main',
                  }}
                />
              </Box>
            </ButtonBase>
          )
        })}
      </Box>
    </Box>
  )
}

export default ThemeDistribution
